/**
 * "Who is submitting" — the name/email pair every submission carries (client, 2026-09-18).
 *
 * Lifted out of `Dropzone.tsx` unchanged, markup and test ids included, when the manual
 * indicator values panel became the second place a person submits something from. The
 * server requires both on `POST /uploads`, which is the first call either surface makes,
 * so the block gates the act rather than being validated at the end: a file dropped — or a
 * grid submitted — before the two fields are answered would open a session the server
 * refuses, and the reader would read that refusal as "my data is wrong".
 *
 * Deliberately uncontrolled about *storage*: remembering the pair is `submitter.ts`'s job
 * and the caller's, because the wizard remembers it on submit and the values panel on the
 * same event — neither wants a field that writes to `localStorage` as it is typed.
 */
import { useTranslation } from 'react-i18next';

import { isEmail } from './manager';

export interface SubmitterFieldsProps {
  name: string;
  email: string;
  onChange: (patch: { name?: string; email?: string }) => void;
  /**
   * Suffix for the field ids and test ids. The default is the wizard's — every test and
   * e2e written against the dropzone still lands on `mgr-f-submitter_name` — and the
   * values panel passes its own so two blocks can never share a DOM id.
   */
  idPrefix?: string;
  /** Test id for the fieldset itself; its hint is `<blockId>-hint`. */
  blockId?: string;
}

export function SubmitterFields({
  name,
  email,
  onChange,
  idPrefix = 'mgr-f',
  blockId = 'mgr-submitter',
}: SubmitterFieldsProps) {
  const { t } = useTranslation();
  const nameId = `${idPrefix}-submitter_name`;
  const emailId = `${idPrefix}-submitter_email`;
  const emailTyped = email.trim() !== '';

  return (
    <fieldset className="mgr-submitter" data-testid={blockId}>
      <legend className="mgr-label">{t('manager.submitter.legend')}</legend>
      <div className="mgr-form">
        <label className="mgr-field" htmlFor={nameId}>
          <span className="mgr-label">
            {t('manager.submitter.name')} <b aria-hidden="true">*</b>
          </span>
          <input
            id={nameId}
            data-testid={nameId}
            className="mgr-input"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </label>
        <label className="mgr-field" htmlFor={emailId}>
          <span className="mgr-label">
            {t('manager.submitter.email')} <b aria-hidden="true">*</b>
          </span>
          <input
            id={emailId}
            data-testid={emailId}
            className="mgr-input"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => onChange({ email: e.target.value })}
          />
          <span className="mgr-hint" data-testid={`${blockId}-hint`}>
            {emailTyped && !isEmail(email)
              ? t('manager.submitter.badEmail')
              : t('manager.submitter.hint')}
          </span>
        </label>
      </div>
    </fieldset>
  );
}

export default SubmitterFields;
